import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  faArrowRotateRight,
  faCirclePlay,
  faInfoCircle,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import TeleophthPatientConfirmationModal from 'src/products/teleophth/pages/TeleophthPatientConfirmationModal/TeleophthPatientConfirmationModal';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomTable from 'src/components/custom_components/CustomTable/CustomTable';
import PatientInfoDrawer from 'src/components/custom_components/PatientInfoDrawer/PatientInfoDrawer';

import { Api } from 'src/models/Api.model';
import { ApplicationInstance } from 'src/models/Application.model';
import { EntityCriteriaOperatorEnum } from 'src/models/GalenData/EntityCriteria.model';
import { PageOfUser } from 'src/models/GalenData/PageOfUser.model';
import { SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';
import { PatientInfoKeyMapOptain, Session } from 'src/models/Session.model';

import useCheckCameraConnection from 'src/hooks/useCheckCameraConnection';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import {
  getPatientFilterConditions,
  sanitizeSearchPatientValues,
} from 'src/utils/patientFilters';

import CameraLanguageDropdown from '../custom_components/CameraLanguageDropdown';
import SitesDropdown from '../custom_components/SitesDropdown';
import WarnNoConnectedCameraModal from '../custom_components/WarnNoConnectedCameraModal';
import SearchPatientInfoFormGroup, {
  SearchPatientFormValues,
} from '../SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

const DEFAULT_PAGE_SIZE = 10;

const NewExam = () => {
  const history = useHistory();
  const { isCameraConnected } = useCheckCameraConnection();

  const {
    practiceSettings: { product },
    session,
  } = useRemoteBoundStore();

  const [formValues, setFormValues] = useState<Partial<Session>>(session);

  const [isShowConfirmModal, setIsShowConfirmModal] = useState<boolean>(false);

  const [showDrawer, setShowDrawer] = useState(false);

  const { selectedSiteId } = useLocalStore();

  const [searchValues, setSearchValues] = useState<SearchPatientFormValues>();

  const [isLoadingTableData, setIsLoadingTableData] = useState(false);

  const [patientsData, setPatientsData] = useState<PageOfUser>();

  const [currentPage, setCurrentPage] = useState(1);

  const { isLoadingSites, existingSites, getExistingSiteData } =
    useGetExistingSiteData();

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const handleCloseConfirmModal = () => {
    setIsShowConfirmModal(false);
  };

  const onSubmit: SubmitHandler<Partial<Session>> = (data) => {
    const dob = moment(data.dob).format('YYYY-MM-DD');
    const age = moment().diff(data.dob, 'years');

    setFormValues({
      ...data,
      dob,
      age,
    });
    setIsShowConfirmModal(true);
  };

  const methods = useForm({
    defaultValues: {
      id: session.id,
      firstName: session.firstName,
      lastName: session.lastName,
      dob: session.dob,
      template: ApplicationInstance(product).templates[0],
      orderId: session.orderId,
    },
  });

  const updateOrderId = (orderId: string) => {
    setFormValues({ ...formValues, orderId });
  };

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);
  const { t: tPages } = useTranslation(I18NextNS.PAGES);

  const appendCustomFields = useCallback(
    async (
      data:
        | AxiosResponse<PageOfUser, any>
        | {
            data: {
              content: never[];
              totalElements: number;
              totalPages: number;
            };
          },
    ) => {
      try {
        if (!data.data?.content || data.data.content.length === 0) {
          setPatientsData({
            content: data.data.content,
            totalElements: data.data.totalElements,
            totalPages: data.data.totalPages,
          });
          return;
        }

        const customFieldsData =
          await Api.remote.get_custom_field_multiple_entities<string>({
            entityIds: data.data.content.map((item) => item.userId),
          });

        if (!customFieldsData.data) {
          // If no custom fields data exists, set all patients data without custom fields.
          // This can happen when patients were created before custom fields were added or updated,
          // such as patients created in versions before 1.0.4 of the app.
          setPatientsData({
            content: data.data.content,
            totalElements: data.data.totalElements,
            totalPages: data.data.totalPages,
          });
          return;
        }

        const processedData = data.data.content.map((user) => {
          const userFields = customFieldsData.data[user.userId];

          // When the userId is not found as a key in customFieldsData.data, userFields will be undefined.
          const sitesField = userFields?.find(
            (field) => field.field.name === 'sites',
          );
          const sitesData = sitesField ? JSON.parse(sitesField.fieldData) : [];

          return {
            ...user,
            sites: sitesData,
          };
        });

        setPatientsData({
          content: processedData,
          totalElements: data.data.totalElements,
          totalPages: data.data.totalPages,
        });
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: errorMessage,
          locale: language,
          type: 'error',
        });
      }
    },
    [handleHttpRequestError, language, t],
  );

  const getPatientsData = useCallback(
    async (
      pageNumber: number,
      pageSize: number,
      siteId: string | undefined,
      searchValues?: SearchPatientFormValues,
    ) => {
      try {
        setIsLoadingTableData(true);

        const filtersWithSiteId = [
          ...getPatientFilterConditions(
            sanitizeSearchPatientValues(searchValues),
          ),
          ...(siteId
            ? [
                {
                  key: import.meta.env.VITE_USER_SITES_FIELD_ID,
                  value: `\\"${siteId}\\"`,
                  operator: EntityCriteriaOperatorEnum.Like,
                  isCustomField: true,
                  isDataValue: false,
                },
              ]
            : []),
        ];

        const data = await Api.remote._get_users_advanced(filtersWithSiteId, {
          pageNumber,
          pageSize,
          sortBy: ['createdOn'],
          sortOrder: SortOrderEnum.DESC,
        });

        await appendCustomFields(data);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: t('bootbox.title.Error'),
          message: errorMessage,
          locale: language,
          type: 'error',
        });
      } finally {
        setIsLoadingTableData(false);
      }
    },
    [appendCustomFields, handleHttpRequestError, language, t],
  );

  const resetSearchValues = () => {
    setSearchValues(undefined);
    getPatientsData(0, DEFAULT_PAGE_SIZE, selectedSiteId);
    setCurrentPage(1);
  };

  useEffect(() => {
    getExistingSiteData();
  }, [getExistingSiteData]);

  useEffect(() => {
    getPatientsData(0, DEFAULT_PAGE_SIZE, selectedSiteId, searchValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Stack gap={3} className="h-100">
        <div className="d-flex flex-column flex-md-row justify-content-md-between gap-3">
          <div className="flex-grow-1">
            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
              <div>
                <h3 style={{ whiteSpace: 'nowrap' }}>
                  {tPages('newExam.desc')}
                </h3>
              </div>
            </div>
            <div className="text-primary d-flex flex-wrap gap-2 align-items-center mt-2">
              <FontAwesomeIcon icon={faCirclePlay} />
              {tPages('newExam.startNewExam')}
              <span>|</span>
              <FontAwesomeIcon icon={faInfoCircle} />
              {t('orderFields.details')}
              <span>|</span>
              <FontAwesomeIcon icon={faArrowRotateRight} />
              {tPages('newExam.resume')}
            </div>
          </div>

          {isCameraConnected && (
            <div className="flex-shrink-0">
              <CameraLanguageDropdown />
            </div>
          )}
        </div>
        <div
          className="flex-grow-1 d-flex flex-column"
          style={{ minHeight: 0 }}
        >
          <div
            className="flex-grow-1 d-flex flex-column"
            style={{ minHeight: 0 }}
          >
            <Stack direction="horizontal" gap={3} className="mb-3">
              <div className="h-100" style={{ paddingTop: '2px' }}>
                <Form.Label>Sites</Form.Label>
                <SitesDropdown
                  toggleClassName="py-2"
                  isLoadingSites={isLoadingSites}
                  existingSites={existingSites}
                  handleSiteChange={(siteId: string | undefined) => {
                    getPatientsData(0, DEFAULT_PAGE_SIZE, siteId, searchValues);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <SearchPatientInfoFormGroup
                onSearchValuesChange={(searchValues) => {
                  setSearchValues(searchValues);
                  getPatientsData(
                    0,
                    DEFAULT_PAGE_SIZE,
                    selectedSiteId,
                    searchValues,
                  );
                  setCurrentPage(1);
                }}
                onFormReset={resetSearchValues}
                values={searchValues}
              />
            </Stack>
            <div
              className="flex-grow-1 d-flex flex-column"
              style={{ minHeight: 0 }}
            >
              <div className="flex-grow-1" style={{ minHeight: 0 }}>
                <CustomTable
                  isLoading={isLoadingTableData}
                  dataSource={patientsData?.content || []}
                  rowKey="userId"
                  pagination={{
                    defaultPageSize: DEFAULT_PAGE_SIZE,
                    pageSizeOptions: [DEFAULT_PAGE_SIZE],
                    totalPages: patientsData?.totalPages ?? 0,
                    currentPage: currentPage,
                    handleSetCurrentPage: setCurrentPage,
                    onChangePage: (page, pageSize) => {
                      getPatientsData(
                        page - 1,
                        pageSize,
                        selectedSiteId,
                        searchValues,
                      );
                    },
                  }}
                  columns={[
                    {
                      title: t('patientFields.DOB'),
                      shouldMask: true,
                      key: 'dateOfBirth',
                    },
                    {
                      title: t('patientFields.First Name'),
                      shouldMask: true,
                      key: 'firstName',
                    },
                    {
                      title: t('patientFields.Last Name'),
                      shouldMask: true,
                      key: 'lastName',
                    },
                    {
                      title: t('patientFields.PID'),
                      shouldMask: true,
                      key: 'patientId',
                    },
                    {
                      title: 'Sites',
                      dataIndex: 'sites',
                      render: (data: GalenDataUser) => {
                        if (!data.sites || data.sites.length === 0) {
                          return '-';
                        }

                        const siteNames = data.sites
                          .map(
                            (site) =>
                              existingSites.find((s) => s.siteId === site)
                                ?.siteName,
                          )
                          .filter((siteName) => siteName)
                          .join(', ');

                        return siteNames || '-';
                      },
                    },
                    {
                      title: t('orderFields.actions'),
                      dataIndex: 'actions',
                      render: (data) => {
                        if (!data) {
                          return;
                        }

                        return (
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              className="rounded-3 px-3 py-1"
                              onClick={() => {
                                setFormValues({
                                  firstName: data.firstName,
                                  lastName: data.lastName,
                                  id: data.patientId ?? '',
                                  dob: data.dateOfBirth,
                                  age: moment().diff(data.dateOfBirth, 'years'),
                                  patientUserId: data.userId,
                                });
                                setShowDrawer(true);
                              }}
                              data-test="select-patient-button"
                            >
                              <FontAwesomeIcon icon={faInfoCircle} />
                            </Button>
                          </div>
                        );
                      },
                    },
                  ]}
                  empty={{
                    buttons: [
                      {
                        icon: (
                          <FontAwesomeIcon icon={faSearch} className="me-2" />
                        ),
                        text: tPages('newExam.searchResults'),
                        onClick: () => history.push('/results'),
                        variant: 'primary-100',
                      },
                    ],
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <TeleophthPatientConfirmationModal
              formValues={formValues}
              isShowConfirmModal={isShowConfirmModal}
              handleCloseConfirmModal={handleCloseConfirmModal}
              InfoConfirmationKeyMap={PatientInfoKeyMapOptain}
            />
          </Form>
        </FormProvider>
      </Stack>

      <PatientInfoDrawer
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        formValues={formValues}
        setShowConfirmModal={setIsShowConfirmModal}
        updateOrderId={updateOrderId}
      />
      <WarnNoConnectedCameraModal isVisible={!isCameraConnected} />
    </>
  );
};

export default NewExam;
